import { ComponentType } from '@angular/cdk/portal';
import { Component, Inject, Injector, Input, OnChanges, OnDestroy, OnInit, Optional, SimpleChanges } from '@angular/core';
import { TT_VIEW_NAVIGATOR, TTNavTarget, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { GetWebpageSettingMenuParams, GetWebpageSettingParams, WebpageSettingMenuGet, WebpageSettingService, WebpageSettingTypeItem } from '@app/services/webpage-setting.service';
import { StateService } from '@uirouter/angularjs';
import { from, ReplaySubject, Subject, switchMap, takeUntil } from 'rxjs';
import { StateService as TTStateService } from '@app/core/services/state.service';

@Component({
    selector: 'tt-webpage-setting-menu',
    templateUrl: './webpage-setting-menu.component.html',
    styleUrls: ['./webpage-setting-menu.component.css'],
})
export class WebpageSettingMenuComponent implements OnInit, OnChanges, OnDestroy {
    @Input()
    public params?: GetWebpageSettingMenuParams;

    public get?: WebpageSettingMenuGet;

    public params$ = new ReplaySubject<GetWebpageSettingMenuParams>(1);

    public destroy$ = new Subject<void>();

    public webpageSettingTypes: WebpageSettingTypeItem[] = [];

    public inputs?: GetWebpageSettingParams;

    public tabContents: { [key: string]: { component: ComponentType<unknown>; injector: Injector } } = {};

    constructor(
        private injector: Injector,
        private state: StateService,
        private ttState: TTStateService,
        private webpageSetting: WebpageSettingService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
    ) {
        this.navigator = useNavigatorFor(navigator, this);

        this.params$
            .pipe(
                takeUntil(this.destroy$),
                switchMap((params) => from(this.webpageSetting.getWebpageSettingMenu(params))),
            )
            .subscribe({
                next: (value) => {
                    this.get = value;
                    this.navigator?.setHeading?.(this.get.page_heading);
                },
            });

        this.params$
            .pipe(
                takeUntil(this.destroy$),
                switchMap((params) => from(this.webpageSetting.getWebpageSettingTypes(params))),
            )
            .subscribe({
                next: (value) => {
                    this.webpageSettingTypes = value;

                    for (let type of this.webpageSettingTypes) {
                        this.tabContents['' + type.item_id] = this.getTabContent(type);
                    }
                },
            });
    }

    trackBy = (_: number, item: WebpageSettingTypeItem) => {
        return item.item_id;
    };

    private getTabContent(item: WebpageSettingTypeItem): { component: ComponentType<unknown>; injector: Injector } {
        if (!this.params) throw 'not ready';
        // @ts-ignore
        const component = this.state.get(item.item_state)?.component;

        if (!component) throw Error('component not found.');

        return {
            component,
            injector: Injector.create({
                providers: [
                    {
                        provide: TT_VIEW_NAVIGATOR,
                        useValue: <TTViewNavigator>{
                            params: { argtype: 'webpage_name', argvalue: this.params.webpage_name },
                            host: 'page',
                            owner: component,
                            sequence: () => {},
                            dismiss: () => {},
                            back: (to) => {
                                this.ttState.back(to);
                            },

                            go: (target: TTNavTarget) => {
                                this.ttState.go(target.to, target.parms, target.options);
                            },
                            newTab: (target: TTNavTarget) => {
                                this.ttState.newTab(target.to, target.parms);
                            },
                        },
                    },
                ],
                parent: this.injector,
            }),
        };
    }

    private async init(params: GetWebpageSettingMenuParams) {
        this.inputs = { argtype: 'webpage', argvalue: params.webpage_name };
        this.params$.next(params);
    }

    ngOnInit(): void {
        if (!!this.navigator) {
            this.navigator.setSize?.({ width: '98vw', height: '95vh' });
            this.params = this.navigator.params as any;
            this.init(this.params!);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['params']) {
            this.init(changes['params'].currentValue);
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.params$.complete();
    }
}
